/* The plan's category and cover, as a popover off the top bar.
 *
 * No KANON specimen draws either in the builder — the canon puts both in the CREATE modal — so the
 * chips and the thumbnail are that modal's own anatomy at popover scale rather than a second
 * vocabulary. Every value is a token; the category colour arrives as `--cat-ink` / `--cat-wash` from
 * `shared/ui/planCategoryColour.ts`, which is what lets six categories share one rule.
 *
 * `--text-3` is not used for text here — see `PhaseBar.module.css` for the measurements. */

/* The cover itself is the trigger, so the control shows what it edits. Sized to the top bar's own
 * control height rather than to the library row's 76×46 crop: this sits in a 32px band beside the
 * heading, and the library's proportions would make it the tallest thing in the row. */
.trigger {
  display: block;
  flex: none;
  padding: var(--space-0);
  overflow: hidden;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-xs);
}

.trigger:hover {
  border-color: var(--text-3);
}

.trigger:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.trigger[data-popup-open] {
  border-color: var(--text-3);
}

.thumb {
  display: block;
  width: 44px;
  height: var(--h-control-icon);
  object-fit: cover;
}

/* Above the day dock's `z-index: 20`, and on the POSITIONER — Base UI positions through inline
 * styles there and the popup inside is `position: static`, so `z-index` on the popup applies to
 * nothing. See `MenuSurface.module.css` for the measured version of that mistake. */
.positioner {
  z-index: 40;
}

/* Bounded and scrolling past the bound, for the reason `shared/ui/MenuSurface.module.css` sets out
 * in full: Base UI positions an anchored popup but never sizes it, so with no `max-height` this one
 * is as tall as its six category chips and its cover picker and simply hangs off the bottom edge on
 * a short window. `--available-height` is the positioner's own trigger-to-edge measurement, which
 * this popup inherits; the fallback matters because an undefined custom property voids the whole
 * declaration rather than falling back to `none`. */
.popup {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 320px;
  max-height: var(--available-height, var(--overlay-max-block));
  padding: var(--space-4);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-pop);
}

.head {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
}

.title {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-load);
  text-transform: uppercase;
  color: var(--text-2);
}

/* The trapped popup's exit — the assign dialog's own close control at popover scale, rather than a
 * second close vocabulary. Base UI asks for one inside any popup that traps focus, so a touch screen
 * reader has somewhere to go. */
.close {
  flex: none;
  width: var(--h-control-icon);
  height: var(--h-control-icon);
  font-size: var(--fs-caption);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-button);
}

.close:hover {
  color: var(--text);
  background: var(--bg-inset);
}

.close:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The cover section's body: the picture, and the line about it. The thumbnail is the library row's
 * own 76 × 46 crop rounded up to the width this column has — near enough that a trainer recognises
 * the same photograph, wide enough not to read as an icon. */
.cover {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.coverThumb {
  display: block;
  flex: none;
  width: 84px;
  height: 56px;
  object-fit: cover;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xs);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-0);
  margin: var(--space-0);
  border: 0;
}

.label {
  padding: var(--space-0);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* The real control, off screen but IN the accessibility tree and in the tab order. `clip-path`
 * rather than `display: none`, which would take the radio out of both. */
.control {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  clip-path: inset(50%);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

/* The create modal's chip, one step smaller — a 320px popover holds six of them on three rows. */
.chip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  padding: var(--space-4px) var(--space-2);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-11px);
}

.chip:hover {
  background: var(--hover-tint);
}

/* Selected takes the category's own wash AND its own edge — two channels, never the hue alone, and
 * the radio's `aria-checked` is the third. */
.chipOn {
  color: var(--cat-ink);
  background: var(--cat-wash);
  border-color: var(--cat-ink);
}

.chipOn:hover {
  background: var(--cat-wash);
}

/* The ring is drawn on the LABEL, because the input it belongs to is off screen. Declared after the
 * chip's own states so the cascade reads top-down by specificity. */
.chip:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.chipDot {
  display: block;
  flex: none;
  width: var(--space-7px);
  height: var(--space-7px);
  background: var(--cat-ink);
  border-radius: var(--radius-hair);
}

/* The line beside the cover since the picker became a statement. It reads as prose rather than as a
 * caption under a grid, so the separating rule that used to sit above it is gone with the grid; the
 * section's own heading is what divides it from the chips. */
.note {
  font-size: var(--fs-caption);
  font-weight: var(--fw-regular);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
}
